// ./src/components/features/PartnerApplications/PartnerApplicationsList/PartnerApplicationsList.tsx
//
// The inbound sponsorship applications on one event (#A4.5) — an inbox, not a CRUD list. A host
// never adds or edits a row; they read it and decide.
//
// ⚠ ONE OF THESE BUTTONS MOVES MONEY. Declining a PAID application refunds the sponsor, so the row
// says "Paid" plainly and the page confirms before it happens. A decline that silently returned
// money would be the single worst thing this screen could do.
//
// Presentation follows the house rule: desktop (≥ md) a real <Table> on its own surface, tablet
// cards with buttons, phone a <SwipeableCard> with none of its own.
import React from "react";
import { Table, Paper, Stack, Group, Text, Badge, ActionIcon, Tooltip, Skeleton, Anchor } from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  AppIcon,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  CardListSkeleton,
  EmptyState,
  LoadMoreButton,
  TableDateTime,
} from "@/components/generic";
import { PartnerKindBadge, PartnerKindIcon } from "@/components/features/EventPartners";
import { useAppEnvironment } from "@/contexts";
import { formatCurrency, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";
import {
  PartnerApplication,
  PARTNER_APPLICATION_STATUS_COLORS,
  partnerApplicationStatusLabel,
  applicationWillRefund,
  applicationIsDecided,
  applicantName,
} from "@/utils/api";
import { APPLICATION_APPROVE_ACTION, APPLICATION_REJECT_ACTION } from "@/hooks/usePartnerApplications";

interface PartnerApplicationsListProps {
  applications: PartnerApplication[];
  loading?: boolean;
  /**
   * ⚠ REQUIRED — whether the FIRST read has come back. A skeleton belongs to that read only; a
   * refetch keeps the rows OR the empty state on screen and lets ContentLoadingOverlay say it is
   * reloading. `loading && items.length === 0` re-showed it on every refetch of an EMPTY list, so
   * an empty page read skeleton → "none" → skeleton → "none". Required so a caller cannot drop it.
   */
  isLoaded: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onOpen: (application: PartnerApplication) => void;
  onApprove: (application: PartnerApplication) => void;
  onReject: (application: PartnerApplication) => void;
  isDeciding: (id?: string, actionKey?: string) => boolean;
  highlightedId?: string | null;
  hasQueryNarrowing?: boolean;
}

/** What they applied for and what it costs — the two numbers a host decides on. */
const DealCell: React.FC<{ application: PartnerApplication }> = ({ application }) => {
  const { t } = useTranslation(["partners"]);

  return (
    <Stack gap={2}>
      <Text size="md" style={{ fontVariantNumeric: "tabular-nums" }}>
        {application.amount != null
          ? formatCurrency(Number(application.amount), application.currencyCode || undefined)
          : t("partners:applications.noPrice", { defaultValue: "—" })}
      </Text>
      {/* ⚠ PAID is the state that makes declining move money — it is never only in the drawer. */}
      {applicationWillRefund(application) ? (
        <Badge size="sm" variant="light" color="teal">
          {t("partners:applications.paid", { defaultValue: "Paid" })}
        </Badge>
      ) : null}
    </Stack>
  );
};

const StatusBadge: React.FC<{ application: PartnerApplication }> = ({ application }) => (
  <Badge
    size="md"
    variant="light"
    color={PARTNER_APPLICATION_STATUS_COLORS[application.applicationStatus || ""] || "gray"}
  >
    {partnerApplicationStatusLabel(application.applicationStatus)}
  </Badge>
);

/**
 * The row actions.
 *
 * ⚠ Approve and Decline disappear once a decision is made — they are not disabled, they are gone.
 * The server refuses a second decision anyway, and a greyed Approve on an approved row invites a
 * click that can only produce an error.
 */
const RowActions: React.FC<{
  application: PartnerApplication;
  onOpen: (a: PartnerApplication) => void;
  onApprove: (a: PartnerApplication) => void;
  onReject: (a: PartnerApplication) => void;
  isDeciding: (id?: string, actionKey?: string) => boolean;
}> = ({ application, onOpen, onApprove, onReject, isDeciding }) => {
  const { t } = useTranslation(["partners", "common"]);
  const approving = isDeciding(application.id, APPLICATION_APPROVE_ACTION);
  const rejecting = isDeciding(application.id, APPLICATION_REJECT_ACTION);
  const busy = isDeciding(application.id);
  const decided = applicationIsDecided(application);

  return (
    <Group gap={4} wrap="nowrap" justify="flex-end">
      <Tooltip label={t("partners:applications.actions.open", { defaultValue: "Open" })} withArrow position="bottom">
        <ActionIcon
          variant="subtle"
          color="blue"
          size="md"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(application);
          }}
        >
          <AppIcon iconName="Eye" size={16} />
        </ActionIcon>
      </Tooltip>

      {decided ? null : (
        <>
          <Tooltip label={t("partners:applications.actions.approve", { defaultValue: "Approve" })} withArrow position="bottom">
            <ActionIcon
              variant="subtle"
              color="green"
              size="md"
              loading={approving}
              disabled={busy && !approving}
              onClick={(e) => {
                e.stopPropagation();
                onApprove(application);
              }}
            >
              <AppIcon iconName="Check" size={16} />
            </ActionIcon>
          </Tooltip>

          <Tooltip
            label={
              applicationWillRefund(application)
                ? t("partners:applications.actions.declineRefund", { defaultValue: "Decline and refund" })
                : t("partners:applications.actions.decline", { defaultValue: "Decline" })
            }
            withArrow
            position="bottom"
          >
            <ActionIcon
              variant="subtle"
              color="red"
              size="md"
              loading={rejecting}
              disabled={busy && !rejecting}
              onClick={(e) => {
                e.stopPropagation();
                onReject(application);
              }}
            >
              <AppIcon iconName="X" size={16} />
            </ActionIcon>
          </Tooltip>
        </>
      )}
    </Group>
  );
};

/** ⚠ Matches the real row cell for cell, so the swap to data causes no layout shift. */
const ApplicationTableRowSkeleton: React.FC = () => (
  <Table.Tr>
    <Table.Td>
      <Skeleton height={16} width="55%" radius="sm" />
      <Skeleton height={12} width="35%" radius="sm" mt={6} />
    </Table.Td>
    <Table.Td>
      <Skeleton height={16} width={140} radius="sm" />
    </Table.Td>
    <Table.Td>
      <Skeleton height={16} width={80} radius="sm" />
    </Table.Td>
    <Table.Td>
      <Skeleton height={16} width={90} radius="sm" />
    </Table.Td>
    <Table.Td>
      <Skeleton height={20} width={90} radius="xl" />
    </Table.Td>
    <Table.Td>
      <Group gap={4} wrap="nowrap" justify="flex-end">
        <Skeleton height={28} circle />
        <Skeleton height={28} circle />
        <Skeleton height={28} circle />
      </Group>
    </Table.Td>
  </Table.Tr>
);

const ApplicationCard: React.FC<{
  application: PartnerApplication;
  highlighted: boolean;
  showActions: boolean;
  onOpen: (a: PartnerApplication) => void;
  onApprove: (a: PartnerApplication) => void;
  onReject: (a: PartnerApplication) => void;
  isDeciding: (id?: string, actionKey?: string) => boolean;
}> = ({ application, highlighted, showActions, onOpen, onApprove, onReject, isDeciding }) => {
  const { t } = useTranslation(["partners"]);

  return (
    <Paper withBorder radius="md" p="md" style={highlighted ? HIGHLIGHT_STYLE : undefined}>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" align="flex-start" wrap="nowrap" style={{ minWidth: 0 }}>
            <PartnerKindIcon kind={application.partnerKind} size={34} />
          <Stack gap={3} style={{ minWidth: 0 }}>
            <Text size="md" fw={600} style={{ overflowWrap: "anywhere" }}>
              {application.orgName || "—"}
            </Text>
            <PartnerKindBadge kind={application.partnerKind} />
          </Stack>
          </Group>
          <StatusBadge application={application} />
        </Group>

        <Group gap="xl" wrap="wrap">
          <DealCell application={application} />
          <Stack gap={2}>
            <Text size="sm" c="dimmed">
              {t("partners:applications.submitted", { defaultValue: "Applied" })}
            </Text>
            <TableDateTime value={application.submittedAt} inline />
          </Stack>
        </Group>

        {application.email ? (
          <Anchor href={`mailto:${application.email}`} size="md" onClick={(e) => e.stopPropagation()}>
            {application.email}
          </Anchor>
        ) : null}

        {showActions ? (
          <RowActions
            application={application}
            onOpen={onOpen}
            onApprove={onApprove}
            onReject={onReject}
            isDeciding={isDeciding}
          />
        ) : null}
      </Stack>
    </Paper>
  );
};

const PartnerApplicationsListComponent: React.FC<PartnerApplicationsListProps> = ({
  applications,
  loading,
  isLoaded,
  loadingMore,
  hasMore,
  onLoadMore,
  onOpen,
  onApprove,
  onReject,
  isDeciding,
  highlightedId,
  hasQueryNarrowing,
}) => {
  const { t } = useTranslation(["partners", "common"]);
  const { isDesktop, isMobile } = useAppEnvironment();
  const { closeAllCards, shouldForceClose, handleForceCloseComplete, openCard } = useSwipeableList();

  // ⚠ The FIRST read only — see `isLoaded` on the props. ▶ npm run check-list-states
  const showSkeleton = !isLoaded;

  if (!showSkeleton && applications.length === 0) {
    return (
      <Paper withBorder p="xl" radius="md">
        {hasQueryNarrowing ? (
          <EmptyState
            title={t("partners:applications.empty.noMatchTitle", { defaultValue: "No applications match" })}
            description={t("partners:applications.empty.noMatchDescription", {
              defaultValue: "Try a different search, or show every application.",
            })}
            iconName="Search"
            size="md"
          />
        ) : (
          <EmptyState
            title={t("partners:applications.empty.title", { defaultValue: "No applications yet" })}
            description={t("partners:applications.empty.description", {
              defaultValue:
                "Sponsors apply from your event website. Add the sponsorship packages block to a page and open a package for applications.",
            })}
            iconName="Inbox"
            size="md"
          />
        )}
      </Paper>
    );
  }

  const loadMore = (
    <LoadMoreButton hasMore={!!hasMore} loading={!!loading} loadingMore={!!loadingMore} onLoadMore={onLoadMore} />
  );

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("partners:applications.columns.applicant", { defaultValue: "Applicant" })}</Table.Th>
                <Table.Th style={{ width: 240 }}>{t("partners:applications.columns.contact", { defaultValue: "Contact" })}</Table.Th>
                <Table.Th style={{ width: 150 }}>{t("partners:applications.columns.deal", { defaultValue: "Package" })}</Table.Th>
                <Table.Th style={{ width: 130 }}>{t("partners:applications.columns.applied", { defaultValue: "Applied" })}</Table.Th>
                <Table.Th style={{ width: 150 }}>{t("partners:applications.columns.status", { defaultValue: "Status" })}</Table.Th>
                <Table.Th style={{ width: 130 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? [0, 1, 2].map((i) => <ApplicationTableRowSkeleton key={i} />)
                : applications.map((application) => (
                    <Table.Tr
                      key={application.id}
                      style={{
                        backgroundColor: application.id === highlightedId ? HIGHLIGHT_ROW_BG : undefined,
                        transition: "background-color 150ms ease",
                      }}
                    >
                      <Table.Td>
                        <Group gap="sm" align="center" wrap="nowrap">
                          <PartnerKindIcon kind={application.partnerKind} size={30} />
                          <Stack gap={3}>
                            <Text size="md" fw={600}>
                              {application.orgName || "—"}
                            </Text>
                            <PartnerKindBadge kind={application.partnerKind} />
                          </Stack>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={0}>
                          {applicantName(application) ? <Text size="md">{applicantName(application)}</Text> : null}
                          {application.email ? (
                            <Anchor href={`mailto:${application.email}`} size="md">
                              {application.email}
                            </Anchor>
                          ) : null}
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <DealCell application={application} />
                      </Table.Td>
                      <Table.Td>
                        <TableDateTime value={application.submittedAt} />
                      </Table.Td>
                      <Table.Td>
                        <StatusBadge application={application} />
                      </Table.Td>
                      <Table.Td>
                        <RowActions
                          application={application}
                          onOpen={onOpen}
                          onApprove={onApprove}
                          onReject={onReject}
                          isDeciding={isDeciding}
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  if (showSkeleton) {
    return <CardListSkeleton rows={3} actions={3} actionsAt="bottom" leading={34} lines={5} lineSizes={['md', 'xs', 'sm', 'xs', 'md']} lineGap={7} />;
  }

  if (!isMobile) {
    return (
      <Stack gap="md">
        <Stack gap="sm">
          {applications.map((application) => (
            <ApplicationCard
              key={application.id}
              application={application}
              highlighted={application.id === highlightedId}
              showActions
              onOpen={onOpen}
              onApprove={onApprove}
              onReject={onReject}
              isDeciding={isDeciding}
            />
          ))}
        </Stack>
        {loadMore}
      </Stack>
    );
  }

  // Phone: the swipe IS the actions.
  // ⚠ Side convention — "do something" (approve / decline) is secondary and lands on the LEFT;
  // Open is the identity action and stays on the right.
  return (
    <Stack gap="md">
      <Stack gap="sm">
        {applications.map((application) => {
          const id = application.id || "";
          const decided = applicationIsDecided(application);
          const actions: SwipeAction[] = [
            ...(decided
              ? []
              : [
                  {
                    key: "approve",
                    label: t("partners:applications.actions.approve", { defaultValue: "Approve" }),
                    icon: "Check",
                    color: "green",
                    secondary: true,
                    loading: isDeciding(id, APPLICATION_APPROVE_ACTION),
                    onClick: () => {
                      closeAllCards();
                      onApprove(application);
                    },
                  },
                  {
                    key: "reject",
                    label: applicationWillRefund(application)
                      ? t("partners:applications.actions.declineRefund", { defaultValue: "Decline and refund" })
                      : t("partners:applications.actions.decline", { defaultValue: "Decline" }),
                    icon: "X",
                    color: "red",
                    secondary: true,
                    loading: isDeciding(id, APPLICATION_REJECT_ACTION),
                    onClick: () => {
                      closeAllCards();
                      onReject(application);
                    },
                  },
                ]),
            {
              key: "open",
              label: t("partners:applications.actions.open", { defaultValue: "Open" }),
              icon: "Eye",
              color: "blue",
              onClick: () => {
                closeAllCards();
                onOpen(application);
              },
            },
          ];

          return (
            <SwipeableCard
              key={id}
              leftSwipeActions={actions}
              forceClose={shouldForceClose(id)}
              onForceCloseComplete={() => handleForceCloseComplete(id)}
              onSwipeStateChange={(isOpen) => {
                if (isOpen) {
                  openCard(id);
                }
              }}
              borderRadius="var(--mantine-radius-md)"
            >
              <ApplicationCard
                application={application}
                highlighted={application.id === highlightedId}
                showActions={false}
                onOpen={onOpen}
                onApprove={onApprove}
                onReject={onReject}
                isDeciding={isDeciding}
              />
            </SwipeableCard>
          );
        })}
      </Stack>
      {loadMore}
    </Stack>
  );
};

// Memoized like the other feature lists — the page re-renders on every keystroke in the search box.
export const PartnerApplicationsList = React.memo(PartnerApplicationsListComponent);
